import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { extractErrorMessage } from '../../utils/errorMessage';

import {
  applyParameterUpdate,
  buildLimitRecordPayload,
  buildLimitRowData,
  clearFieldsForLimitMode,
  createDataSourceConfiguration,
  createLimitDraft,
  DataSourceConfiguration,
  getWizardSteps,
  InvestmentComplianceLimitDataLevel,
  LimitParameter,
  LimitSetupDraft,
  needsDenominator,
} from './createInvestmentLimitModel';
import { clearUnavailableKpis } from './investmentLimitKpiAvailability';
import { isWizardStepValid } from './investmentLimitValidation';

type UseInvestmentLimitWizardArgs = {
  dataLevels: readonly InvestmentComplianceLimitDataLevel[];
  onApply: (data: Record<string, unknown>) => Promise<void>;
  onClose: () => void;
  rowColumnKeys: readonly string[];
};

export function useInvestmentLimitWizard({
  dataLevels,
  onApply,
  onClose,
  rowColumnKeys,
}: UseInvestmentLimitWizardArgs) {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);
  const [draft, setDraft] = useState<LimitSetupDraft>(createLimitDraft);
  const [isSaving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const includeDenominator = needsDenominator(draft);
  const wizardSteps = useMemo(
    () => getWizardSteps(includeDenominator),
    [includeDenominator]
  );
  const activeStepKey = wizardSteps[activeStep]?.key ?? 'overview';

  const transferPayload = useMemo(
    () => buildLimitRecordPayload(draft),
    [draft]
  );
  const rowPayload = useMemo(
    () => buildLimitRowData(transferPayload, rowColumnKeys),
    [rowColumnKeys, transferPayload]
  );

  const canContinue = isWizardStepValid(activeStepKey, draft, dataLevels);

  const resetAndClose = useCallback(() => {
    setActiveStep(0);
    setDraft(createLimitDraft());
    setError(null);
    setSaving(false);
    onClose();
  }, [onClose]);

  const updateDraft = useCallback((update: Partial<LimitSetupDraft>) => {
    setDraft((currentDraft) => {
      if (
        update.limitMode == null ||
        update.limitMode === currentDraft.limitMode
      ) {
        return { ...currentDraft, ...update };
      }

      return {
        ...currentDraft,
        ...update,
        parameter: clearUnavailableKpis(
          clearFieldsForLimitMode(currentDraft.parameter, update.limitMode),
          update.limitMode
        ),
      };
    });
  }, []);

  const updateParameter = useCallback((update: Partial<LimitParameter>) => {
    setDraft((currentDraft) => ({
      ...currentDraft,
      parameter: clearUnavailableKpis(
        applyParameterUpdate(currentDraft.parameter, update),
        currentDraft.limitMode
      ),
    }));
  }, []);

  const updateDenominator = useCallback(
    (update: Partial<DataSourceConfiguration>) => {
      setDraft((currentDraft) => ({
        ...currentDraft,
        parameter: clearUnavailableKpis(
          {
            ...currentDraft.parameter,
            denominator: {
              ...(currentDraft.parameter.denominator ??
                createDataSourceConfiguration()),
              ...update,
            },
          },
          currentDraft.limitMode
        ),
      }));
    },
    []
  );

  const createLimit = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      await onApply(rowPayload);
      resetAndClose();
    } catch (submitError) {
      setError(
        extractErrorMessage(submitError, t('app.investmentLimit.createFailed'))
      );
      setSaving(false);
    }
  }, [onApply, resetAndClose, rowPayload, t]);

  const handlePrimaryAction = useCallback(() => {
    if (!canContinue || isSaving) {
      return;
    }

    if (activeStep < wizardSteps.length - 1) {
      setActiveStep((currentStep) => currentStep + 1);
      return;
    }

    void createLimit();
  }, [activeStep, canContinue, createLimit, isSaving, wizardSteps.length]);

  const goBack = useCallback(() => {
    setError(null);
    setActiveStep((currentStep) => currentStep - 1);
  }, []);

  return {
    activeStep,
    activeStepKey,
    canContinue,
    draft,
    error,
    goBack,
    handlePrimaryAction,
    includeDenominator,
    isSaving,
    resetAndClose,
    updateDenominator,
    updateDraft,
    updateParameter,
    wizardSteps,
  };
}
